import { useLocation, useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Icon from '@mui/material/Icon'
import CardMedia from '@mui/material/CardMedia'
import Skeleton from '@mui/material/Skeleton'

// Local imports
import AdxSortTable from 'components/reusable/table/AdxSortTable'
import { iconConstructor } from 'pages/approval-center/functions'
import defaultAvatar from 'assets/images/default_avatar.png'
import AdxStatusBar from 'components/reusable/statusBar/AdxStatusBar'
import leaveRequestStatusHandler from 'module/leave/util/leaveRequestStatusHandler'

import AdxEmptyStateV2 from 'components/reusable/emptyState/AdxEmptyStateV2'

const LeaveRespondedTab = ({
  respondedData,
  isLoading,
  error,
  pagination,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const hasRespondedLeaves = (respondedData || []).length > 0

  const upcomingRequestColumn = [
    {
      Header: 'Request',
      accessor: 'leave_policy_name',
      Cell: (props) => {
        const data = props.row.original
        return (
          <Box
            display={'flex'}
            justifyContent={'space-between'}
            alignItems={'center'}
            columnGap={3}
          >
            <Box display="flex" alignItems="center" columnGap={2}>
              <Box
                display={'flex'}
                justifyContent={'center'}
                alignItems={'center'}
                width={50}
                height={50}
                bgcolor="alternate.light"
                borderRadius={'50%'}
              >
                <Icon sx={{ fontSize: 25, color: 'alternate.main' }}>
                  {iconConstructor(data?.leave_type === 'Leave Request' ? 1 : 2)}
                </Icon>
              </Box>
              <Box>
                <Typography
                  width={{
                    xs: 'calc(100vw - 400px)',
                    lg: 'calc(100vw - 1200px)',
                  }}
                  minWidth={{ xs: 1, lg: '200px' }}
                  noWrap
                  variant="body1"
                  color="dark-text.main "
                  textTransform="capitalize"
                >
                  {data?.leave_policy_name}
                </Typography>

                <Box display={'flex'} alignItems={'center'} columnGap={1.5}>
                  {data?.type ? (
                    <>
                      <Typography
                        noWrap
                        component="span"
                        variant="body2"
                        color="light-gray-text.main"
                        textTransform="capitalize"
                      >
                        {data?.type}
                      </Typography>

                      <Icon sx={{ fontSize: 7, color: 'light-gray-text.main' }}>
                        circle_rounded
                      </Icon>
                    </>
                  ) : null}
                  <Typography
                    noWrap
                    component="span"
                    variant="body2"
                    color="light-gray-text.main"
                    textTransform="capitalize"
                  >
                    {data?.start_date} - {data?.end_date}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )
      },
    },
    {
      Header: 'Status',
      accessor: 'request_status',
      Cell: (props) => {
        const data = props.row.original
        return (
          <Box
            display={'flex'}
            flexDirection="column"
            justifyContent="center"
            rowGap={0.5}
            sx={{ pl: 1.5 }}
          >
            <AdxStatusBar status={leaveRequestStatusHandler(data?.request_status)} />
          </Box>
        )
      },
    },
    {
      Header: 'Amount',
      accessor: 'duration',
      Cell: (props) => {
        const data = props.row.original
        return (
          <Box
            display={'flex'}
            flexDirection="column"
            justifyContent="center"
            rowGap={0.5}
            sx={{ pl: 1.5 }}
          >
            <Typography align="left" variant="body1" color="dark-text.main" noWrap>
              {data?.duration} {data?.duration === 1 ? 'day' : 'days'}
            </Typography>
          </Box>
        )
      },
    },
    {
      Header: 'Requester',
      accessor: 'request_from',
      Cell: (props) => {
        const data = props.row.original
        return (
          <Box display={'flex'} alignItems="center" columnGap={1} sx={{ pl: 2 }}>
            <CardMedia
              component="img"
              sx={{
                height: 40,
                width: 40,
                bgcolor: 'gray-background.main',
                objectFit: 'cover',
                borderRadius: '50%',
                border: '3px solid',
                borderColor: 'white.main',
              }}
              alt={data?.request_from}
              src={data?.request_from_image || defaultAvatar}
            />

            <Typography variant="body1" color="dark-text.main" align="left" noWrap>
              {data?.request_from}
            </Typography>
          </Box>
        )
      },
    },
  ]
  const navigate = useNavigate()
  const backgroundLocation = useLocation()

  if (isLoading) {
    return (
      <Box
        height={138}
        width={1}
        bgcolor="white.main"
        borderRadius={1}
        padding={{ xs: 2, lg: 3 }}
        boxSizing="border-box"
        sx={{ boxShadow: 'none' }}
      >
        <Box height={1} display="flex" justifyContent="space-between" alignItems="center">
          <Box
            height={1}
            width={{ xs: 1, lg: 4 / 5 }}
            display="flex"
            alignItems="center"
            columnGap={2}
          >
            <Box display="flex" justifyContent="center" alignItems="center" width={90} height={90}>
              <Skeleton variant="circular" width={60} height={60} />
            </Box>
            <Box height={1} display="flex" flexDirection="column" justifyContent="space-between">
              <Box>
                <Skeleton variant="text" width={200} />
                <Skeleton variant="text" width={200} />
              </Box>
              <Skeleton variant="text" width={200} />
            </Box>
          </Box>
          <Skeleton variant="text" width={100} sx={{ display: { xs: 'none', lg: 'block' } }} />
        </Box>
      </Box>
    )
  }

  if (!hasRespondedLeaves) {
    return (
      <AdxEmptyStateV2
        title={
          error?.status === 403
            ? 'You are not authorized to access this feature. Please contact your admin for access.'
            : 'Nothing to show here'
        }
      />
    )
  }

  return (
    <AdxSortTable
      nav="View"
      onClick={(e) =>
        navigate(`/app/approval-center/leave/request/${e.id}`, {
          state: {
            backgroundLocation,
            data: {
              id: e.id,
              type: e.leave_type === 'Leave Request' ? 1 : 2,
              title: e.leave_policy_name,
              subtitle: e.type,
              startDate: e.start_date,
              endDate: e.end_date,
              user: e.request_from,
              imgUrl: e.request_from_image,
              amount: e.duration,
              requestId: e.leave_request_id,
            },
          },
        })
      }
      column={upcomingRequestColumn}
      data={respondedData}
      pagination={Boolean(pagination)}
      count={pagination?.total}
      page={page}
      onPageChange={onPageChange}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={onRowsPerPageChange}
      height={'calc(100vh - 235px)'}
    />
  )
}

export default LeaveRespondedTab
